/* =====================================================================
   LA PANTALLA DE MANTENIMIENTO · Entrega B (20 sep 2026)
   =====================================================================
   Cuando hay un mantenimiento programado EN CURSO y el servidor no
   contesta, esto tapa la aplicación: marca de TEGO, qué está pasando,
   cuánto lleva, cuánto falta (estimado), y que los datos están a salvo.
   Progreso indeterminado suave, sin rebotes; respeta «reducir movimiento».
   SVG y CSS propios, cero terceros.

   CUÁNDO SALE, y es a propósito que sean las dos cosas a la vez: dentro
   de la ventana (desde 3 min antes del inicio hasta 2 h después del fin
   estimado) Y con `/api/salud` sin contestar. Antes de la hora, un fallo
   de red es «sin conexión», no mantenimiento; y si el servidor contesta
   durante la ventana, la banda lo dice en tono fuerte y la gente sigue
   trabajando.

   CUÁNDO SE QUITA: cuando `/api/salud` vuelve a contestar (se sondea cada
   10 s) se enseña «Ya está» unos segundos y se retira sola; si el
   servidor marcó el mantenimiento como terminado, igual. Si pasa del
   estimado, lo dice con el número, en vez de quedarse girando.

   La monta el integrador en `main.jsx`, dentro del proveedor de sesión y
   antes de `App`, para que tape también la pantalla de acceso.
   ===================================================================== */
import { useEffect, useState } from 'react';
import { useMantenimiento, MARGEN_ANTES_MS } from './mantenimiento';
import './mantenimiento.css';

const CADA_SONDEO_MS = 10 * 1000;
const YA_ESTA_MS = 5 * 1000;

// Con la máquina apagada, una petición puede quedarse colgada un minuto
// antes de fallar (el proxy espera, el TCP espera). Para esta pantalla
// «no contesta en 5 segundos» ES no contestar.
const ESPERA_SALUD_MS = 5000;

async function servidorContesta() {
  const ctl = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const t = ctl ? setTimeout(() => ctl.abort(), ESPERA_SALUD_MS) : null;
  try {
    const r = await fetch('/api/salud', { cache: 'no-store', signal: ctl ? ctl.signal : undefined });
    return r.ok;
  } catch {
    return false;
  } finally {
    if (t) clearTimeout(t);
  }
}

function minutos(ms) { return Math.max(0, Math.round(ms / 60000)); }

export default function PantallaMantenimiento() {
  const { programado, fase, ahoraServidor, releer } = useMantenimiento();
  const [caido, setCaido] = useState(false);
  const [yaEsta, setYaEsta] = useState(false);
  const inicio = programado ? new Date(programado.inicio).getTime() : NaN;
  const fin = programado ? new Date(programado.fin_estimado || 0).getTime() : NaN;
  const enVentana = !!programado && fase !== 'terminado' && fase !== null && fase !== 'lejos'
    && ahoraServidor >= inicio - MARGEN_ANTES_MS;

  // Dentro de la ventana se sondea el servidor; fuera, no se gasta red.
  useEffect(() => {
    let vivo = true;
    if (!enVentana) {
      // Fuera de la ventana no hay nada que sondear; si quedó marcado
      // «caído» de una ventana anterior, se limpia en el siguiente tic.
      const t0 = setTimeout(() => { if (vivo) setCaido(false); }, 0);
      return () => { vivo = false; clearTimeout(t0); };
    }
    const sondear = async () => {
      const ok = await servidorContesta();
      if (!vivo) return;
      if (ok && caido) { setYaEsta(true); releer(); return; }
      if (!ok) setCaido(true);
    };
    sondear();
    const t = setInterval(sondear, CADA_SONDEO_MS);
    return () => { vivo = false; clearInterval(t); };
  }, [enVentana, caido, releer]);

  // «Ya está» se enseña unos segundos y la pantalla se retira sola. Vive en
  // su propio efecto: el de arriba se rearma al cambiar `caido`, y un
  // temporizador suyo moriría con él.
  useEffect(() => {
    if (!yaEsta) return undefined;
    const t = setTimeout(() => { setYaEsta(false); setCaido(false); }, YA_ESTA_MS);
    return () => clearTimeout(t);
  }, [yaEsta]);

  if (!programado || (!caido && !yaEsta)) return null;
  if (fase === 'terminado' && !yaEsta) return null;

  const lleva = minutos(ahoraServidor - inicio);
  const faltan = minutos(fin - ahoraServidor);
  const pasado = ahoraServidor > fin;
  const deMas = minutos(ahoraServidor - fin);
  const reducido = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div role="alert" aria-live="polite" data-pantalla="mantenimiento" data-estado={yaEsta ? 'ya-esta' : pasado ? 'pasado' : 'en-curso'}
         className="consola fixed inset-0 z-[100] flex items-center justify-center p-6 bg-white text-gray-900"
>
      <div className="w-full max-w-md text-center">
        <svg viewBox="0 0 168 32" width="168" height="32" aria-label="TEGO Cloud" role="img" className="mx-auto mb-6">
          <text x="0" y="24" fontFamily="'IBM Plex Sans', system-ui, sans-serif" fontWeight="700" fontSize="24" letterSpacing="-0.6" fill="currentColor">TEGO</text>
          <text x="70" y="24" fontFamily="'IBM Plex Sans', system-ui, sans-serif" fontWeight="700" fontSize="24" letterSpacing="-0.6" fill="var(--color-marca)">Cloud</text>
        </svg>
        {yaEsta ? (
          <>
            <h1 className="text-2xl font-semibold mb-2">Ya está: TEGO volvió.</h1>
            <p className="text-gray-600">Puedes seguir trabajando. Esta pantalla se quita sola.</p>
          </>
        ) : (
          <>
            <h1 className="text-2xl font-semibold mb-2">Estamos actualizando TEGO</h1>
            <p className="text-gray-700 mb-4">{programado.texto}</p>
            <div className="h-1.5 w-full rounded-full overflow-hidden bg-gray-200" aria-hidden="true">
              <div className={'h-full w-1/3 rounded-full bg-[var(--color-marca)] ' + (reducido ? 'opacity-70' : 'tego-progreso-indeterminado')} />
            </div>
            <p className="mt-4 text-sm text-gray-600" data-tiempos>
              {pasado
                ? `Está tardando más de lo estimado: lleva ${lleva} min, ${deMas} min más de lo previsto. Seguimos en ello; no hace falta que hagas nada.`
                : `Lleva ${lleva} min · faltan unos ${faltan} min (estimado).`}
            </p>
            <p className="mt-3 text-sm text-gray-600">
              Tus datos están a salvo: todo lo que ya guardaste está guardado. Lo que estés capturando ahora, espera a que vuelva.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
